import { useCallback, useEffect, useRef, useState } from 'react';
import type { RecipePanel, RecipeStep, ScaledIngredient } from '../api/types';
import StepText from './StepText';

type Props = {
  recipeId: number;
  steps: RecipeStep[];
  panels: RecipePanel[];
  ingredients: ScaledIngredient[];
  servings: number;
  onClose: () => void;
};

const SWIPE_THRESHOLD_PX = 50;
const PANEL_WIDTH = 900;

function panelSrc(recipeId: number, index: number, part: 'photo' | 'text' = 'photo') {
  const suffix = part === 'text' ? '&part=text' : '';
  return `/api/recipes/${recipeId}/panel/${index}/?w=${PANEL_WIDTH}${suffix}`;
}

/** Consecutive panels for one step, as one pane: a step that runs on into a
 * second column, and a variant's reprint shown beside the card's own. */
function groupByStep(panels: RecipePanel[]): RecipePanel[][] {
  const groups: RecipePanel[][] = [];
  for (const panel of panels) {
    const last = groups[groups.length - 1];
    if (last && panel.step_number !== null && last[0].step_number === panel.step_number)
      last.push(panel);
    else groups.push([panel]);
  }
  return groups;
}

/**
 * Full-screen, one-pane-per-step viewer for cooking from a phone.
 *
 * Pane 0 is the ingredient checklist ("mise en place"); each following pane is
 * a single step. Navigation is swipe, tap zones, or arrow keys, and the screen
 * is kept awake while it's open.
 */
export default function StepViewer({
  recipeId,
  steps,
  panels,
  ingredients,
  servings,
  onClose,
}: Props) {
  const [index, setIndex] = useState(0);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [showText, setShowText] = useState(false);
  const [brokenPanels, setBrokenPanels] = useState<Set<number>>(new Set());
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  /* Panes come from the card's own panels where they were found, joined to the
     extracted steps by the printed number. Where they weren't — one style is a
     flattened scan with no text layer to read geometry from — the extracted
     text is the pane, exactly as before. */
  const panes: { panels: RecipePanel[]; step: RecipeStep | null }[] =
    panels.length > 0
      ? groupByStep(panels).map((group) => ({
          panels: group,
          step: steps.find((s) => s.number === group[0].step_number) ?? null,
        }))
      : steps.map((step) => ({ panels: [], step }));

  const total = panes.length + 1;

  const go = useCallback(
    (delta: number) =>
      setIndex((current) => Math.min(Math.max(current + delta, 0), total - 1)),
    [total],
  );

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'ArrowRight' || event.key === ' ') go(1);
      else if (event.key === 'ArrowLeft') go(-1);
      else if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  // Hands are busy and covered in oil; don't let the screen sleep mid-step.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    let closed = false;

    async function acquire() {
      if (closed || document.visibilityState !== 'visible') return;
      try {
        lock = (await navigator.wakeLock?.request('screen')) ?? null;
        if (closed) {
          void lock?.release();
          lock = null;
        }
      } catch {
        /* unsupported or denied — not worth surfacing */
      }
    }

    // The browser drops the lock whenever the tab hides, and a glance at a
    // timer app is enough to hide it. Nothing gives it back but us.
    function onVisibility() {
      if (document.visibilityState === 'visible') void acquire();
    }

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void lock?.release();
    };
  }, []);

  // One pane ahead, so the next step is up before a floury thumb asks for it —
  // without pulling every panel on the card the moment the viewer opens.
  useEffect(() => {
    const next = panes[index]; // index 0 is the checklist, so this is pane+1
    for (const panel of next?.panels ?? []) {
      new Image().src = panelSrc(recipeId, panel.index);
      if (panel.has_text) new Image().src = panelSrc(recipeId, panel.index, 'text');
    }
  }, [index, panes, recipeId]);

  function onTouchStart(event: React.TouchEvent) {
    // Two fingers is a pinch to zoom into a photo, not a swipe to the next
    // step. Without this the browser's own zoom fights the pane change.
    if (event.touches.length !== 1) {
      touchStart.current = null;
      return;
    }
    touchStart.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
  }

  function onTouchEnd(event: React.TouchEvent) {
    const start = touchStart.current;
    touchStart.current = null;
    if (start === null) return;
    if (event.touches.length > 0) return;
    const deltaX = event.changedTouches[0].clientX - start.x;
    const deltaY = event.changedTouches[0].clientY - start.y;
    // A drag down a tall panel is a scroll, and no thumb travels straight; a
    // step that changed because a scroll drifted sideways loses your place.
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (Math.abs(deltaX) > SWIPE_THRESHOLD_PX) go(deltaX < 0 ? 1 : -1);
  }

  function toggle(id: number) {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const pane = index > 0 ? panes[index - 1] : null;
  const step = pane?.step ?? null;
  const shown = (pane?.panels ?? []).filter((panel) => !brokenPanels.has(panel.index));

  return (
    <div
      className="viewer"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="region"
      aria-label="Step by step"
    >
      <header className="viewer__bar">
        <button className="viewer__close" onClick={onClose} aria-label="Close step view">
          ✕
        </button>
        <span className="viewer__count">
          {index === 0
            ? 'Ingredients'
            : `Step ${step?.number ?? index} of ${panes.length}`}
        </span>
        <span className="viewer__servings">{servings} servings</span>
      </header>

      <div className="viewer__progress" aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className={i <= index ? 'is-done' : ''} />
        ))}
      </div>

      <div className="viewer__pane" key={index}>
        {index === 0 ? (
          <>
            <h2>Get everything out</h2>
            <ul className="checklist">
              {ingredients.map((item) => (
                <li key={item.ingredient_id}>
                  <label className={checked.has(item.ingredient_id) ? 'is-checked' : ''}>
                    <input
                      type="checkbox"
                      checked={checked.has(item.ingredient_id)}
                      onChange={() => toggle(item.ingredient_id)}
                    />
                    <span>
                      {item.quantity_display && <strong>{item.quantity_display} </strong>}
                      {item.unit && <strong>{item.unit} </strong>}
                      {item.name}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        ) : shown.length > 0 ? (
          <>
            {shown.map((panel) => (
              <div key={panel.index}>
                {/* A variant can show two panels for one step: the card's
                    own, and the one it reprints for the swap. Unlabelled they
                    read as the same step printed twice. */}
                {panel.swap_label && (
                  <p className="viewer__swap-note">
                    The <strong>{panel.swap_label}</strong> version of this step
                  </p>
                )}
                {/* The panel as it is printed. This is what "pane by pane" was
                    always supposed to mean — the card itself, photograph and
                    all, rather than a transcription of it. */}
                <img
                  className="viewer__panel"
                  src={panelSrc(recipeId, panel.index)}
                  alt={step?.title || `Step ${panel.step_number ?? panel.index + 1}`}
                  onError={() =>
                    setBrokenPanels((current) => new Set(current).add(panel.index))
                  }
                />
                {/* Cards that print their instructions away from the
                    photographs need the second cut too, or the pane is a
                    picture with no words. */}
                {panel.has_text && (
                  <img
                    className="viewer__panel viewer__panel--text"
                    src={panelSrc(recipeId, panel.index, 'text')}
                    alt={
                      step?.title
                        ? `${step.title}, as printed`
                        : `Step ${panel.step_number ?? panel.index + 1}, as printed`
                    }
                  />
                )}
              </div>
            ))}
            {step && (
              <>
                <button
                  type="button"
                  className="link-button viewer__toggle"
                  aria-expanded={showText}
                  onClick={() => setShowText(!showText)}
                >
                  {showText ? 'Hide the text' : 'Read as text'}
                </button>
                {showText && <StepText step={step} ingredients={ingredients} />}
              </>
            )}
          </>
        ) : (
          <>
            <h2>{step?.title || `Step ${step?.number}`}</h2>
            {step && <StepText step={step} ingredients={ingredients} />}
          </>
        )}
      </div>

      <nav className="viewer__nav">
        <button className="btn" onClick={() => go(-1)} disabled={index === 0}>
          ← Back
        </button>
        <button
          className="btn btn--primary"
          onClick={() => (index === total - 1 ? onClose() : go(1))}
        >
          {index === total - 1 ? 'Done' : 'Next →'}
        </button>
      </nav>
    </div>
  );
}
